import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { formatCurrency } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Users, DollarSign, Link as LinkIcon, TrendingUp, Copy, Check, ExternalLink, Banknote, Globe, Edit } from 'lucide-react';
import { Link } from 'react-router-dom';

const PartnerDashboardPage = () => {
  const [data, setData] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [withdrawForm, setWithdrawForm] = useState({ valor: '', pix_key: '', observacao: '' });
  const [withdrawing, setWithdrawing] = useState(false);

  const loadAll = () => {
    Promise.all([
      api.get('/partner/dashboard'),
      api.get('/partner/referrals'),
      api.get('/partner/commissions'),
      api.get('/partner/withdrawals').catch(() => ({ data: [] })),
    ]).then(([d, r, c, w]) => {
      setData(d.data);
      setReferrals(r.data || []);
      setCommissions(c.data || []);
      setWithdrawals(w.data || []);
    }).catch(() => toast.error('Erro ao carregar dashboard'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadAll(); }, []);

  const copyLink = () => {
    if (!data?.referral_link) return;
    navigator.clipboard.writeText(data.referral_link);
    setCopied(true);
    toast.success('Link copiado!');
    setTimeout(() => setCopied(false), 2000);
  };

  const openWithdraw = () => {
    setWithdrawForm({
      valor: String(data?.current_balance || 0),
      pix_key: data?.partner?.email || '',
      observacao: '',
    });
    setShowWithdrawModal(true);
  };

  const submitWithdraw = async () => {
    const valor = parseFloat(withdrawForm.valor);
    if (!valor || valor <= 0) {
      toast.error('Informe um valor válido');
      return;
    }
    if (valor > (data?.current_balance || 0) + 0.005) {
      toast.error('Valor excede o saldo disponível');
      return;
    }
    if (!withdrawForm.pix_key || withdrawForm.pix_key.trim().length < 3) {
      toast.error('Informe a chave PIX para receber');
      return;
    }
    setWithdrawing(true);
    try {
      await api.post('/partner/withdrawals', {
        valor,
        pix_key: withdrawForm.pix_key,
        observacao: withdrawForm.observacao,
      });
      toast.success('Solicitação de saque enviada! Você receberá em até 5 dias úteis.');
      setShowWithdrawModal(false);
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao solicitar saque');
    } finally {
      setWithdrawing(false);
    }
  };

  if (loading || !data) {
    return <div className="p-8">Carregando...</div>;
  }

  return (
    <div className="p-8 max-w-7xl mx-auto" data-testid="partner-dashboard-page">
      <div className="mb-8">
        <h1 className="text-4xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Painel do Parceiro
        </h1>
        <p className="text-slate-600 mt-1">Olá, <strong>{data.partner.name}</strong> — indique clientes e ganhe comissão recorrente</p>
      </div>

      {/* Link de referência */}
      <Card className="mb-6 border-emerald-200 bg-emerald-50">
        <CardHeader>
          <div className="flex items-center justify-between gap-3">
            <CardTitle className="flex items-center gap-2">
              <LinkIcon className="w-5 h-5 text-emerald-600" />
              Seu link de indicação
            </CardTitle>
            <Link to="/parceiro/minisite">
              <Button size="sm" variant="outline" className="border-emerald-300 text-emerald-700 hover:bg-emerald-100" data-testid="btn-editar-minisite">
                <Globe className="w-4 h-4 mr-1" />
                Editar meu Minisite
              </Button>
            </Link>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-600 mb-3">
            Compartilhe este link. Cada cliente que se cadastrar por ele fica vinculado a você
            e você ganha <strong>comissão TODO MÊS enquanto ele pagar a mensalidade</strong>.
            Você também tem um <strong>minisite público com SEO</strong> pra divulgar em redes sociais e Google:
            <br />
            <a href={`/p/${data.partner.ref_code}`} target="_blank" rel="noopener noreferrer" className="text-emerald-700 hover:underline font-medium" data-testid="link-meu-minisite">
              nfcesys.com.br/p/{data.partner.ref_code}
            </a>
          </p>
          <div className="flex gap-2 items-center bg-white p-3 rounded border">
            <code className="flex-1 text-sm break-all" data-testid="referral-link">{data.referral_link}</code>
            <Button size="sm" onClick={copyLink} data-testid="copy-link-btn">
              {copied ? <Check className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
              {copied ? 'Copiado!' : 'Copiar'}
            </Button>
            <Button size="sm" variant="outline" asChild>
              <a href={data.referral_link} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="w-4 h-4" />
              </a>
            </Button>
          </div>
          <p className="text-xs text-slate-500 mt-2">
            Código: <strong>{data.partner.ref_code}</strong>
          </p>
        </CardContent>
      </Card>

      {/* Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card data-testid="card-total-referrals">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-600 flex items-center gap-2">
              <Users className="w-4 h-4 text-blue-500" /> Indicados
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-blue-600">{data.total_referrals}</div>
            <p className="text-xs text-slate-500 mt-1">{data.active_referrals} ativo(s)</p>
          </CardContent>
        </Card>

        <Card data-testid="card-current-balance">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-600 flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-emerald-500" /> Saldo atual
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-emerald-600">{formatCurrency(data.current_balance)}</div>
            <Button
              size="sm"
              className="w-full mt-2 bg-emerald-600 hover:bg-emerald-700"
              disabled={(data.current_balance || 0) <= 0}
              onClick={openWithdraw}
              data-testid="btn-solicitar-saque"
            >
              <Banknote className="w-4 h-4 mr-1" />
              Solicitar Saque
            </Button>
          </CardContent>
        </Card>

        <Card data-testid="card-month">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-600 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-amber-500" /> Este mês
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-amber-600">{formatCurrency(data.commissions_this_month?.total || 0)}</div>
            <p className="text-xs text-slate-500 mt-1">{data.commissions_this_month?.count || 0} comissões</p>
          </CardContent>
        </Card>

        <Card data-testid="card-total-earned">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Total ganho</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-slate-800">{formatCurrency(data.total_earned)}</div>
            <p className="text-xs text-slate-500 mt-1">Histórico completo</p>
          </CardContent>
        </Card>
      </div>

      {/* Referrals */}
      <Card className="mb-6">
        <CardHeader><CardTitle>Meus indicados ({referrals.length})</CardTitle></CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm" data-testid="referrals-table">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="text-left p-3">Empresa</th>
                <th className="text-left p-3">Email</th>
                <th className="text-left p-3">Plano</th>
                <th className="text-left p-3">Status</th>
                <th className="text-left p-3">Cadastrado em</th>
              </tr>
            </thead>
            <tbody>
              {referrals.length === 0 ? (
                <tr><td colSpan="5" className="p-6 text-center text-slate-500">Nenhum indicado ainda. Compartilhe seu link!</td></tr>
              ) : referrals.map((r) => (
                <tr key={r.id} className="border-b">
                  <td className="p-3 font-medium">{r.company_name}</td>
                  <td className="p-3 text-slate-600">{r.email}</td>
                  <td className="p-3">{r.plan_id}</td>
                  <td className="p-3">
                    <span className={`text-xs px-2 py-1 rounded ${
                      r.subscription_status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'
                    }`}>{r.subscription_status || 'trial'}</span>
                  </td>
                  <td className="p-3 text-slate-600">{r.created_at ? new Date(r.created_at).toLocaleDateString('pt-BR') : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Comissões */}
      <Card>
        <CardHeader><CardTitle>Histórico de comissões ({commissions.length})</CardTitle></CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm" data-testid="commissions-table">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="text-left p-3">Mês</th>
                <th className="text-left p-3">Plano</th>
                <th className="text-right p-3">Valor venda</th>
                <th className="text-right p-3">%</th>
                <th className="text-right p-3">Comissão</th>
                <th className="text-left p-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {commissions.length === 0 ? (
                <tr><td colSpan="6" className="p-6 text-center text-slate-500">Nenhuma comissão ainda</td></tr>
              ) : commissions.map((c) => (
                <tr key={c.id} className="border-b">
                  <td className="p-3">{c.reference_month}</td>
                  <td className="p-3">{c.plan_id}</td>
                  <td className="p-3 text-right">{formatCurrency(c.valor_venda)}</td>
                  <td className="p-3 text-right">{c.rate_percent}%</td>
                  <td className="p-3 text-right text-emerald-600 font-medium">{formatCurrency(c.commission_amount)}</td>
                  <td className="p-3"><span className={`text-xs px-2 py-1 rounded ${
                    c.status === 'paid' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                  }`}>{c.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
      {/* Saques */}
      <Card className="mb-6">
        <CardHeader><CardTitle className="flex items-center gap-2"><Banknote className="w-5 h-5 text-emerald-600" /> Meus saques ({withdrawals.length})</CardTitle></CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm" data-testid="withdrawals-table">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="text-left p-3">Solicitado em</th>
                <th className="text-right p-3">Valor</th>
                <th className="text-left p-3">Chave PIX</th>
                <th className="text-left p-3">Status</th>
                <th className="text-left p-3">Processado em</th>
                <th className="text-left p-3">Observação</th>
              </tr>
            </thead>
            <tbody>
              {withdrawals.length === 0 ? (
                <tr><td colSpan="6" className="p-6 text-center text-slate-500">Nenhum saque solicitado. Quando tiver saldo, clique em &quot;Solicitar Saque&quot;.</td></tr>
              ) : withdrawals.map((w) => (
                <tr key={w.id} className="border-b" data-testid={`withdrawal-row-${w.id}`}>
                  <td className="p-3">{new Date(w.requested_at).toLocaleString('pt-BR')}</td>
                  <td className="p-3 text-right font-semibold text-emerald-700">{formatCurrency(w.valor)}</td>
                  <td className="p-3 text-slate-600 text-xs break-all">{w.pix_key || '-'}</td>
                  <td className="p-3">
                    <span className={`text-xs px-2 py-1 rounded ${
                      w.status === 'paid' ? 'bg-emerald-100 text-emerald-700' :
                      w.status === 'rejected' ? 'bg-rose-100 text-rose-700' :
                      'bg-amber-100 text-amber-700'
                    }`}>
                      {w.status === 'paid' ? 'Pago' : w.status === 'rejected' ? 'Rejeitado' : 'Pendente'}
                    </span>
                  </td>
                  <td className="p-3 text-slate-600">{w.processed_at ? new Date(w.processed_at).toLocaleString('pt-BR') : '-'}</td>
                  <td className="p-3 text-slate-500 text-xs">{w.admin_note || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Modal Solicitar Saque */}
      <Dialog open={showWithdrawModal} onOpenChange={setShowWithdrawModal}>
        <DialogContent data-testid="withdraw-modal">
          <DialogHeader>
            <DialogTitle>Solicitar Saque</DialogTitle>
            <DialogDescription>
              Seu saldo disponível é <strong>{formatCurrency(data.current_balance)}</strong>.
              O pagamento é feito via PIX em até 5 dias úteis após aprovação.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Valor (R$) *</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                max={data.current_balance}
                value={withdrawForm.valor}
                onChange={(e) => setWithdrawForm({ ...withdrawForm, valor: e.target.value })}
                data-testid="input-withdraw-valor"
              />
            </div>
            <div>
              <Label>Chave PIX (CPF, email ou telefone) *</Label>
              <Input
                value={withdrawForm.pix_key}
                onChange={(e) => setWithdrawForm({ ...withdrawForm, pix_key: e.target.value })}
                placeholder="Ex.: seu@email.com ou 000.000.000-00"
                data-testid="input-withdraw-pix"
              />
            </div>
            <div>
              <Label>Observação (opcional)</Label>
              <Input
                value={withdrawForm.observacao}
                onChange={(e) => setWithdrawForm({ ...withdrawForm, observacao: e.target.value })}
                data-testid="input-withdraw-obs"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowWithdrawModal(false)}>Cancelar</Button>
            <Button onClick={submitWithdraw} disabled={withdrawing} className="bg-emerald-600 hover:bg-emerald-700" data-testid="btn-confirm-withdraw">
              {withdrawing ? 'Enviando...' : 'Confirmar Saque'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PartnerDashboardPage;
